import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { QuickReviewListItem } from "../../../../data-model/quickReviewModel";
import {
  TableCellMultiline,
  TableCellWithBadge,
} from "../../../../data-model/tableCells";
import { getBadgePropsForAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { QuickReviewTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { capitalizeString } from "../../../../utils/stringModifications";
import { MedicalPaOrdersAlgoliaSortFields } from "../../medicalPaOrdersAlgolia/utils/medicalPaOrdersTable";

/**
 * Column schema and row mapping for the Quick Review worklist table.
 * @module quickReviewTableUtils
 */

/** One Quick Review board row, keyed by {@link QuickReviewTableColumnKeys}. */
export interface QuickReviewTableData {
  [QuickReviewTableColumnKeys.PATIENT_DETAILS]: TableCellMultiline;
  [QuickReviewTableColumnKeys.CREATED_AT]: string;
  [QuickReviewTableColumnKeys.DATE_OF_SERVICE]: string;
  [QuickReviewTableColumnKeys.BO_VALUE_WITH_RADIO_SELECT]: TableCellWithBadge;
  [QuickReviewTableColumnKeys.AUTH_STATUS]: TableCellWithBadge;
  [QuickReviewTableColumnKeys.ASSIGNEE]: string;
  [QuickReviewTableColumnKeys.ROW_DATA]: string;
  [QuickReviewTableColumnKeys.ID]: string;
}

export class QuickReviewTableUtils {
  /**
   * Column headers for the Quick Review board table.
   *
   * Restricted to the columns available in
   * {@link QuickReviewTableColumnKeys}.
   */
  static generateColumnsForQuickReviewTable(): TableHeader[] {
    return [
      {
        label: "Patient Name",
        key: QuickReviewTableColumnKeys.PATIENT_DETAILS,
        order: 1,
        width: 18,
        sortable: true,
        type: TableCellType.MULTILINE,
        subKey: "mainText",
        sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
      },
      {
        label: "Date of Creation",
        key: QuickReviewTableColumnKeys.CREATED_AT,
        order: 2,
        width: 18,
        sortable: true,
        type: TableCellType.DATE,
        sortField: MedicalPaOrdersAlgoliaSortFields.CREATED_AT,
      },
      {
        label: "DOS",
        key: QuickReviewTableColumnKeys.DATE_OF_SERVICE,
        order: 3,
        width: 14,
        sortable: true,
        type: TableCellType.DATE,
        sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
      },
      {
        label: "Estimate Value",
        key: QuickReviewTableColumnKeys.BO_VALUE_WITH_RADIO_SELECT,
        order: 4,
        width: 16,
        type: TableCellType.BADGE,
      },
      {
        label: "Auth Status",
        key: QuickReviewTableColumnKeys.AUTH_STATUS,
        order: 5,
        width: 16,
        type: TableCellType.BADGE_WITH_I,
      },
      {
        label: "Assigned To",
        key: QuickReviewTableColumnKeys.ASSIGNEE,
        order: 6,
        width: 18,
        type: TableCellType.STRING,
      },
    ];
  }

  /**
   * Transforms Quick Review board items into table row objects consumable by
   * CustomTable. Maps patient name, creation date (board row
   * `updated_at`), DOS, estimate/BO value badge, auth status badge, and
   * assignee into the Quick Review column schema.
   *
   * @param items - Board rows from the Quick Review list endpoint
   * @param authStatusOptions - Available auth status options for badge rendering
   * @returns Array of table row data objects keyed by QuickReviewTableColumnKeys
   */
  static generateTableDataFromQuickReviewItems(
    items: QuickReviewListItem[],
    authStatusOptions: AuthStatusOptionModel[],
  ): QuickReviewTableData[] {
    return items.map((item) => {
      const authStatus = getBadgePropsForAuthStatus(
        authStatusOptions,
        item.auth_status,
      );

      return {
        [QuickReviewTableColumnKeys.PATIENT_DETAILS]: {
          mainText: capitalizeString(
            generateFullName(item.first_name ?? "", "", item.last_name ?? ""),
          ),
          secondaryText: `MRN : ${item.patient_id ?? ""}`,
        },
        [QuickReviewTableColumnKeys.CREATED_AT]: formatDateToUTCWithFormat(
          item.updated_at ?? "",
          "MM/DD/YYYY",
          "",
        ),
        [QuickReviewTableColumnKeys.DATE_OF_SERVICE]: formatDateToUTCWithFormat(
          item.date_of_service ?? "",
          "MM/DD/YYYY",
          "",
        ),
        [QuickReviewTableColumnKeys.BO_VALUE_WITH_RADIO_SELECT]: {
          text: capitalizeString(item.estimate_value ?? ""),
        },
        [QuickReviewTableColumnKeys.AUTH_STATUS]: {
          text: authStatus?.text ?? "",
          color: authStatus?.textColor ?? "",
          bgColor: authStatus?.bgColor ?? "",
        },
        [QuickReviewTableColumnKeys.ASSIGNEE]: item.assigned_to_name ?? "",
        [QuickReviewTableColumnKeys.ROW_DATA]: JSON.stringify(item),
        [QuickReviewTableColumnKeys.ID]: item.order_id ?? "",
      };
    });
  }
}
